import { Box, keyframes } from '@mui/material';
import { MOTION_DURATION_MS, MOTION_EASING, REDUCED_MOTION_QUERY } from '../theme/motion';

// Both strokes declare `pathLength={1}`: dash values are fractions of the
// stroke, independent of the drawn geometry.
const drawStroke = keyframes`
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

interface StrokeAnimation {
  animate: boolean;
  durationMs: number;
  delayMs: number;
}

function getStrokeAnimationSx({ animate, durationMs, delayMs }: StrokeAnimation) {
  return {
    strokeDasharray: 1,
    strokeDashoffset: 0,
    animation: animate
      ? `${drawStroke} ${durationMs}ms ${MOTION_EASING.arrive} ${delayMs}ms both`
      : 'none',
    [REDUCED_MOTION_QUERY]: { animation: 'none' },
  };
}

interface DrawnCheckMarkProps {
  /** Draw the mark stroke by stroke; when false it renders already drawn. */
  animate: boolean;
  size?: number;
  /** Any MUI `sx` color value; the strokes use `currentColor`. */
  color?: string;
  /** In viewBox units (the viewBox is 24x24). */
  strokeWidth?: number;
  circleDelayMs?: number;
  /** The tick starts after the circle has begun closing, so the eye reads circle-then-tick. */
  tickDelayMs?: number;
  tickDurationMs?: number;
}

export function DrawnCheckMark({
  animate,
  size = 24,
  color = 'success.main',
  strokeWidth = 2,
  circleDelayMs = 0,
  tickDelayMs = 120,
  tickDurationMs = MOTION_DURATION_MS.layout,
}: DrawnCheckMarkProps) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      sx={{
        display: 'block',
        flexShrink: 0,
        width: size,
        height: size,
        color,
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        '& .drawn-check-mark__circle': getStrokeAnimationSx({
          animate,
          durationMs: MOTION_DURATION_MS.layout,
          delayMs: circleDelayMs,
        }),
        '& .drawn-check-mark__tick': getStrokeAnimationSx({
          animate,
          durationMs: tickDurationMs,
          delayMs: tickDelayMs,
        }),
      }}
    >
      {/* Two arcs starting at 12 o'clock, drawn clockwise. */}
      <path
        className="drawn-check-mark__circle"
        d="M12 2a10 10 0 1 1 0 20a10 10 0 1 1 0-20"
        pathLength={1}
      />
      <path className="drawn-check-mark__tick" d="M7.5 12.5l3 3l6-6.5" pathLength={1} />
    </Box>
  );
}
